import { useState, useEffect, useCallback } from 'react';
import {
  getActivityLog,
  type ActivityLogFilters,
  type PaginationParams,
  type ActivityLogEntry,
} from '@/shared/db';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function useActivityLog(
  initialFilters: ActivityLogFilters = {},
  initialPagination: PaginationParams = { page: 1, pageSize: 50 }
) {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ActivityLogFilters>(initialFilters);
  const [pagination, setPagination] = useState<PaginationParams>(initialPagination);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { activities: activitiesData, total: totalCount } = await getActivityLog(
        filters,
        pagination
      );

      setActivities(activitiesData);
      setTotal(totalCount);
    } catch (err: unknown) {
      console.error('Error fetching activity log:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.fetchActivitiesFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, t]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const updateFilters = (newFilters: Partial<ActivityLogFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination({ ...pagination, page: 1 }); // Reset to first page
  };

  const updatePagination = (newPagination: Partial<PaginationParams>) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  return {
    activities,
    total,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    updatePagination,
    refetch: fetchActivities,
  };
}

